import { IAttributeDefinitionEntity } from '../../../../types/entities/IAttributeDefinitionEntity';
import { IAttributeValue } from '../../../../types/entities/IAttributeValue';
import { IHotel } from '../../../../types/entities/IHotel';
import { IHotelImage } from '../../../../types/entities/IHotelImage';
import { IServiceType } from '../../../../types/entities/IServiceType';
import { ISupplierHotel } from '../../../../types/entities/ISupplierHotel';
import { convertDateToUTC } from '../../../../utils/formatDates';
import { getRandomNumber } from '../../../../utils/usefulFunctions';
import {
    ActivityLogTypeKey,
    AttributeDefinitionFieldType,
    AttributeDefinitionSizeKeyType,
    AttributeDefinitionState,
    AttributeValueState,
    HotelContentState,
    HotelImageState,
    ZinantisServiceState,
} from '../../types/zitoneoServicesTypes';
import {
    getFieldType,
    sortAttributeDefinitions,
    DEFAULT_CHARACTER_LIMIT,
    COMPETITORS_ZITONEO_SCRAPING_EXECUTION_TYPE_KEY,
} from '../../utils/zitoneoServicesUtils';
import { adaptFileState } from '../../utils/imageUtils';

export const adaptDDBBHotelImageToState = (
    image: IHotelImage
): HotelImageState => {
    return {
        Id: image.Id,
        Category:
            image.HotelImageType?.LocalizedCaption ||
            image.HotelImageType?.Caption ||
            image.HotelImageType?.Name ||
            '',
        IdCategory: image.IdHotelImageType,
        CategoryKey: image.HotelImageType?.Key || '',
        Url: image.VirtualPath,
        Caption: image.Caption,
        Feature: image.Feature,
        Published: image.Published,
        IdHotelRoomType: image.IdHotelRoomType,
        IdSupplierHotelRoomType: image.IdSupplierHotelRoomType,
    };
};

export const adaptDDBBServiceTypeToState = (
    service: Partial<IServiceType>
): Partial<ZinantisServiceState> => {
    return {
        ServiceTypeTranslations: service?.ContentLocalized?.map(
            translation => ({
                Id: translation.Id,
                IdLanguage: translation.IdContentLanguage,
                LanguageCode: translation.ContentLanguage?.CultureId || '',
                Name: translation?.LocalizedCaption || '',
            })
        ),
        ServiceCategoryTranslations:
            service?.ServiceCategory?.ContentLocalized?.map(translation => ({
                Id: translation.Id,
                IdLanguage: translation.IdContentLanguage,
                LanguageCode: translation.ContentLanguage?.CultureId || '',
                Name: translation?.LocalizedCaption || '',
            })),
    };
};

export const adaptDDBBAttributeDefinitionToState = (
    attributeDefinition: Partial<IAttributeDefinitionEntity>
): AttributeDefinitionState => {
    const isStringField = attributeDefinition.AttributeDefinition?.FieldType
        ? attributeDefinition.AttributeDefinition.FieldType.IsString
        : true;

    return {
        Id: attributeDefinition.Id || '',
        IdAttributeDefinition: attributeDefinition?.IdAttributeDefinition || 0,
        Required: attributeDefinition.Required || false,
        FieldType: attributeDefinition?.AttributeDefinition?.FieldType
            ? getFieldType(attributeDefinition?.AttributeDefinition?.FieldType)
            : AttributeDefinitionFieldType.STRING,
        Name: attributeDefinition.AttributeDefinition?.Name || '',
        NameTranslations:
            attributeDefinition.AttributeDefinition?.ContentLocalized?.map(
                translation => ({
                    Id: translation.Id,
                    IdLanguage: translation.IdContentLanguage,
                    LanguageCode: translation.ContentLanguage?.CultureId || '',
                    Name: translation?.LocalizedName || '',
                })
            ) || [],
        Unique: isStringField
            ? attributeDefinition.AttributeDefinition?.MustBeUnique || false
            : true,
        SizeKey:
            (attributeDefinition.AttributeDefinition
                ?.AttributeDefinitionSizeType
                ?.Key as AttributeDefinitionSizeKeyType) ||
            AttributeDefinitionSizeKeyType.SMALL,
        CharacterLimit:
            attributeDefinition.AttributeDefinition?.AttributeDefinitionSizeType
                ?.Size || DEFAULT_CHARACTER_LIMIT,
        IdCategory:
            attributeDefinition.AttributeDefinition
                ?.IdAttributeDefinitionCategory || 0,
        Category:
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .LocalizedCaption ||
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .Caption ||
            attributeDefinition.AttributeDefinition?.AttributeDefinitionCategory
                .Name ||
            '',
    };
};

export const adaptDDBBAttributeValueToState = (
    defValue: IAttributeValue
): AttributeValueState => {
    return {
        Id: defValue.Id,
        IdAttributeDefinition: defValue.IdAttributeDefinition,
        Value: defValue.Value || '',
        IsString: defValue.AttributeDefinition?.FieldType?.IsString || false,
    };
};

export const adaptDDBBAttributeValuesAndCreateNewOnesToState = (
    defValues: IAttributeValue[],
    definitions: IAttributeDefinitionEntity[]
): AttributeValueState[] => {
    const adaptedValues = defValues.map(defValue =>
        adaptDDBBAttributeValueToState(defValue)
    );

    const newBooleanValues = definitions
        .filter(definition => {
            const hasValue = adaptedValues.some(
                value =>
                    value.IdAttributeDefinition ===
                    definition.IdAttributeDefinition
            );

            return (
                definition.AttributeDefinition?.FieldType?.IsBoolean &&
                !hasValue
            );
        })
        .map(definition => ({
            Id: `-${getRandomNumber()}`,
            IdAttributeDefinition: definition.IdAttributeDefinition,
            Value: 'False',
            ContentTranslations: [],
            IsString: false,
            __isAdded: true,
            __isChanged: true,
        }));

    return [...adaptedValues, ...newBooleanValues];
};

export const adaptDDBBSupplierHotelContentToState = (
    hotelContent: Partial<ISupplierHotel>
): HotelContentState => {
    return {
        Id: hotelContent.Id || '',
        IdHotel: hotelContent.IdHotel || 0,
        Name: hotelContent.Name || '',
        HotelOwner: hotelContent.HotelOwner?.Name || '',
        Address: hotelContent.Hotel?.Address || '',
        City: hotelContent.Hotel?.City?.Name || '',
        Country: hotelContent.Hotel?.Country?.Name || '',
        GiataCode: hotelContent.Hotel?.HotelCodes?.GiataCode || '',
        HotelServices: [],
        LastChange: '',
        LastChangeBy: '',
        Rooms: hotelContent.Hotel?.Rooms || 0,
        ZitoneoMemberDate: hotelContent.Hotel?.ZitoneoMemberDate || '',
        ZitoneoMembership: hotelContent.Hotel?.ZitoneoMembership || false,
        Category:
            hotelContent.Hotel?.HotelCategory?.LocalizedCaption ||
            hotelContent.Hotel?.HotelCategory?.Caption ||
            hotelContent.Hotel?.HotelCategory?.Name ||
            '',
        CategoryKey: hotelContent.Hotel?.HotelCategory?.Key || '',
        ContractType: {
            Name:
                hotelContent.Hotel?.ZitoneoContractType?.LocalizedCaption ||
                hotelContent.Hotel?.ZitoneoContractType?.Caption ||
                hotelContent.Hotel?.ZitoneoContractType?.Name ||
                '',
            Translations:
                hotelContent.Hotel?.ZitoneoContractType?.ContentLocalized?.map(
                    translation => ({
                        Id: translation.Id,
                        IdLanguage: translation.IdContentLanguage,
                        LanguageCode:
                            translation.ContentLanguage?.CultureId || '',
                        Name: translation.LocalizedCaption || '',
                    })
                ) || [],
        },
        ZitoneoPublishStatus:
            hotelContent.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.LocalizedCaption ||
            hotelContent.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Caption ||
            hotelContent.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Name ||
            '',
        ZitoneoPublishStatusKey:
            hotelContent.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Key || '',
        ZitoneoPublishStatusColor:
            hotelContent.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Color || '',
        URLs: [],
        HotelImages: [],
        HotelRoomTypes: [],
        ActivityLogs: [],
        IdLogoFile: hotelContent.Hotel?.IdLogoFile || 0,
        LogoFile: adaptFileState(
            hotelContent.Hotel?.LogoFile,
            hotelContent.Hotel?.IdLogoFile || 0
        ),
        IdIconFile: hotelContent.Hotel?.IdZitoneoIconFile || 0,
        IconFile: adaptFileState(
            hotelContent.Hotel?.ZitoneoIconFile,
            hotelContent.Hotel?.IdZitoneoIconFile || 0
        ),
        ZitoneoScrapingResults: [],
        ...adaptDDBBHotelContentToState(hotelContent.Hotel as Partial<IHotel>),
    };
};

export const adaptDDBBHotelContentToState = (
    hotelContent: Partial<IHotel>
): Partial<HotelContentState> => {
    const logoId = hotelContent?.IdLogoFile || 0;
    const iconId = hotelContent?.IdZitoneoIconFile || 0;

    return {
        ZitoneoPublishStatus:
            hotelContent?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.LocalizedCaption ||
            hotelContent?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Caption ||
            hotelContent?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Name ||
            '',
        ZitoneoPublishStatusKey:
            hotelContent?.ZitoneoPublishStatus?.ZitoneoPublishStatusType?.Key ||
            '',
        ZitoneoPublishStatusColor:
            hotelContent?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                ?.Color || '',
        ZitoneoMemberDate: hotelContent?.ZitoneoMemberDate || '',
        ZitoneoMembership: hotelContent?.ZitoneoMembership || false,
        IdLogoFile: logoId,
        LogoFile: adaptFileState(hotelContent?.LogoFile, logoId),
        IdIconFile: iconId,
        IconFile: adaptFileState(hotelContent?.ZitoneoIconFile, iconId),
        LastChange: hotelContent.HotelZitoneoChange?.ChangeDate || '',
        LastChangeBy:
            hotelContent.HotelZitoneoChange?.SystemUser?.Name?.split('@')[0] ||
            '',
        Address: hotelContent.Address || '',
        Rooms: hotelContent.Rooms || 0,
        HotelServices:
            hotelContent?.HotelServiceTypes?.map(service => ({
                Id: service.Id,
                IdServiceType: service.IdServiceType,
                IdServiceCategory: service.ServiceType.IdServiceCategory,
                Name: service.Name || '',
                Description: service.Description || '',
                ServiceType:
                    service.ServiceType.LocalizedCaption ||
                    service.ServiceType.Caption ||
                    service.ServiceType.Name ||
                    '',
                ServiceCategory:
                    service.ServiceType.ServiceCategory.LocalizedCaption ||
                    service.ServiceType.ServiceCategory.Caption ||
                    service.ServiceType.ServiceCategory.Name ||
                    '',
                ServiceTypeTranslations:
                    service.ServiceType.ContentLocalized.map(translation => ({
                        Id: translation.Id,
                        IdLanguage: translation.IdContentLanguage,
                        LanguageCode:
                            translation.ContentLanguage?.CultureId || '',
                        Name: translation?.LocalizedCaption || '',
                    })),
                ServiceCategoryTranslations:
                    service.ServiceType.ServiceCategory.ContentLocalized.map(
                        translation => ({
                            Id: translation.Id,
                            IdLanguage: translation.IdContentLanguage,
                            LanguageCode:
                                translation.ContentLanguage?.CultureId || '',
                            Name: translation?.LocalizedCaption || '',
                        })
                    ),
                AttributeDefinitions: sortAttributeDefinitions(
                    service.AttributeDefinitionEntities.map(
                        attributeDefinition =>
                            adaptDDBBAttributeDefinitionToState(
                                attributeDefinition
                            )
                    ) || []
                ),
                AttributeValues:
                    adaptDDBBAttributeValuesAndCreateNewOnesToState(
                        service.AttributeDefinitionValues,
                        service.AttributeDefinitionEntities
                    ),
                Exceptions: service.ServiceExceptions.map(exception => ({
                    Id: exception.Id,
                    IdHotelService: exception.IdHotelService,
                    Active: exception.Active,
                    Description: exception.Description,
                    Dates: exception.Dates.map(date => ({
                        Id: date.Id,
                        Active: date.Active,
                        FromDate: date.FromDate,
                        ToDate: date.ToDate,
                    })),
                    IdServiceExceptionLevelType:
                        exception.IdServiceExceptionLevelType || 0,
                })),
            })) || [],
        URLs:
            hotelContent?.HotelZitoneoUrls?.map(url => ({
                Id: url.Id,
                Enabled: url.Enabled,
                IsPrimary: url.IsPrimary,
                Path: url.Path,
            })) || [],
        HotelRoomTypes:
            hotelContent?.HotelRoomTypes?.filter(
                roomType => roomType.IdMasterRoomType && roomType.Enabled
            ).map(roomType => {
                const supplierHotelRooms = roomType.SupplierRoomTypes.filter(
                    supplierRoomType => supplierRoomType.Enabled
                ).map(supplierRoomType => ({
                    Id: supplierRoomType.Id,
                    Enabled: supplierRoomType.Enabled,
                    IdSupplierHotelRoom:
                        supplierRoomType.IdSupplierHotelRoomType,
                    SupplierHotelRoom:
                        supplierRoomType.SupplierHotelRoomType.Name,
                    MappingCode:
                        supplierRoomType.SupplierHotelRoomType.MappingCode,
                }));
                const masterRoomName = roomType.MasterRoomType?.SpecialRoom
                    ? roomType.MasterRoomType?.ManualName
                    : roomType.MasterRoomType?.AutoName || '';

                const mainSupplierRoom = supplierHotelRooms[0];
                const roomSupplierNameText = mainSupplierRoom
                    ? `${mainSupplierRoom.MappingCode} - ${mainSupplierRoom.SupplierHotelRoom}`
                    : masterRoomName;
                return {
                    Id: roomType.Id,
                    IdSupplierHotelRoom:
                        mainSupplierRoom?.IdSupplierHotelRoom || 0,
                    MasterRoomName: masterRoomName,
                    Label: roomSupplierNameText,
                    MainSupplierHotelMappingCode:
                        mainSupplierRoom?.MappingCode || '',
                    MainSupplierHotelRoomName:
                        mainSupplierRoom?.SupplierHotelRoom || masterRoomName,
                    HotelImages: roomType.HotelImages.map(
                        adaptDDBBHotelImageToState
                    ),
                    Services: roomType.RoomTypeServices.filter(
                        service => service.Enabled
                    ).map(service => ({
                        Id: service.Id,
                        IdServiceType: service.IdServiceType || 0,
                        IdServiceCategory:
                            service.ServiceType?.IdServiceCategory || 0,
                        ServiceType:
                            service.ServiceType?.LocalizedCaption ||
                            service.ServiceType?.Caption ||
                            service.ServiceType?.Name ||
                            '',
                        ServiceCategory:
                            service.ServiceType?.ServiceCategory
                                ?.LocalizedCaption ||
                            service.ServiceType?.ServiceCategory?.Caption ||
                            service.ServiceType?.ServiceCategory?.Name ||
                            '',
                        ServiceTypeTranslations:
                            service.ServiceType?.ContentLocalized?.map(
                                translation => ({
                                    Id: translation.Id,
                                    IdLanguage: translation.IdContentLanguage,
                                    LanguageCode:
                                        translation.ContentLanguage
                                            ?.CultureId || '',
                                    Name: translation.LocalizedCaption || '',
                                })
                            ) || [],
                        ServiceCategoryTranslations:
                            service.ServiceType?.ServiceCategory?.ContentLocalized?.map(
                                translation => ({
                                    Id: translation.Id,
                                    IdLanguage: translation.IdContentLanguage,
                                    LanguageCode:
                                        translation.ContentLanguage
                                            ?.CultureId || '',
                                    Name: translation.LocalizedCaption || '',
                                })
                            ) || [],
                        AttributeDefinitions: sortAttributeDefinitions(
                            service.AttributeDefinitionEntities?.map(
                                attributeDefinition =>
                                    adaptDDBBAttributeDefinitionToState(
                                        attributeDefinition
                                    )
                            ) || []
                        ),
                        AttributeValues:
                            adaptDDBBAttributeValuesAndCreateNewOnesToState(
                                service.AttributeDefinitionValues || [],
                                service.AttributeDefinitionEntities || []
                            ),
                        Description: service.Description,
                    })),
                    SupplierHotelRooms: supplierHotelRooms,
                };
            }) || [],
        HotelImages:
            hotelContent?.HotelImages?.map(adaptDDBBHotelImageToState) || [],
        ActivityLogs: (hotelContent?.ZitoneoActivityLogs || [])
            .map(log => ({
                Id: log.Id,
                Agency: log.Agency.Name,
                TypeKey: log.ZitoneoAPIActivityType.Key as ActivityLogTypeKey,
                Type:
                    log.ZitoneoAPIActivityType.LocalizedCaption ||
                    log.ZitoneoAPIActivityType.Caption ||
                    log.ZitoneoAPIActivityType.Name ||
                    '',
                DateCreated: log.DateCreated,
                Failed: log.Failed,
                FailedMessage: log.FailedMessage,
                Message: log.Message,
            }))
            .sort(
                (firstLog, secondLog) =>
                    // Sort by DateCreated in descending order
                    new Date(secondLog.DateCreated).getTime() -
                    new Date(firstLog.DateCreated).getTime()
            ),
        ZitoneoScrapingResults: (
            hotelContent?.ZitoneoScrapingExecutions || []
        ).flatMap(execution =>
            (execution.Sections || [])
                .map(section => ({
                    Id: execution.Id,
                    ExecutionDate: new Date(execution.ExecutionDate),
                    DateLastUpdated: new Date(execution.DateLastUpdated),
                    Result: execution.Result,
                    IdProcess: execution.IdProcess,
                    Process: execution.Process?.Name || '',
                    ProcessStatusKey:
                        execution.Process?.ProcessStatus?.ProcessStatusType
                            ?.Key || '',
                    ProcessStatus:
                        execution.Process?.ProcessStatus?.ProcessStatusType
                            ?.Caption || '',
                    IdSection: section.Id,
                    IdFile: section.IdFile,
                    File: section.File?.Name || '',
                    IdZitoneoScrapingExecutionType:
                        section.IdZitoneoScrapingExecutionType,
                    ZitoneoScrapingExecutionTypeKey:
                        section.ZitoneoScrapingExecutionType?.Key || '',
                    FileDescription: section.File?.Description || '',
                }))
                .filter(
                    result =>
                        result.ZitoneoScrapingExecutionTypeKey ===
                        COMPETITORS_ZITONEO_SCRAPING_EXECUTION_TYPE_KEY
                )
        ),
    };
};

export const adaptStateHotelContentToDDBB = (
    hotelContent: Partial<HotelContentState>,
    idSysUser: number
) => {
    return {
        Id: hotelContent.IdHotel?.toString(),
        HotelZitoneoChange: {
            Id: `-${getRandomNumber()}`,
            ChangeDate: new Date(),
            IdSystemUser: idSysUser,
            IdHotel: hotelContent.IdHotel,
            __isAdded: true,
            __isChanged: true,
        },
        HotelServiceTypes: (hotelContent.HotelServices || [])
            .map(service => ({
                Id: service.Id,
                IdServiceType: service.IdServiceType,
                Name: service.Name || '',
                Description: service.Description || '',
                AttributeDefinitionValues: service?.AttributeValues?.map(
                    value => ({
                        Id: value.Id,
                        IdAttributeDefinition: value.IdAttributeDefinition,
                        Value: value.Value ? value.Value.toString() : '',
                        __isAdded: parseInt(value.Id) < 0 || value.__isAdded,
                        __isChanged:
                            value.__isChanged ||
                            parseInt(value.Id) < 0 ||
                            value.__isAdded ||
                            service.__isChanged,
                        Deleted: value.Deleted,
                    })
                ).filter(
                    attributeValue =>
                        attributeValue.Deleted ||
                        attributeValue.__isAdded ||
                        attributeValue.__isChanged
                ),
                ServiceExceptions: service.Exceptions.map(exception => ({
                    Id: exception.Id,
                    IdHotelService: exception.IdHotelService,
                    Active: exception.Active,
                    Description: exception.Description,
                    Dates: exception.Dates.map(date => ({
                        Id: date.Id,
                        Active: date.Active,
                        FromDate: date.FromDate
                            ? convertDateToUTC(date.FromDate)
                            : null,
                        ToDate: date.ToDate
                            ? convertDateToUTC(date.ToDate)
                            : null,
                        __isAdded: parseInt(date.Id) < 0 || date.__isAdded,
                        __isChanged:
                            date.__isChanged ||
                            parseInt(date.Id) < 0 ||
                            date.__isAdded ||
                            date.__isChanged,
                        Deleted: date.Deleted,
                    })).filter(
                        date =>
                            date.Deleted || date.__isAdded || date.__isChanged
                    ),
                    IdServiceExceptionLevelType:
                        exception.IdServiceExceptionLevelType || 0,
                    __isAdded:
                        parseInt(exception.Id) < 0 || exception.__isAdded,
                    __isChanged:
                        exception.__isChanged ||
                        parseInt(exception.Id) < 0 ||
                        exception.__isAdded ||
                        service.__isChanged,
                    Deleted: exception.Deleted,
                })).filter(
                    exc => exc.Deleted || exc.__isAdded || exc.__isChanged
                ),
                __isAdded: parseInt(service.Id) < 0 || service.__isAdded,
                __isChanged:
                    service.__isChanged ||
                    parseInt(service.Id) < 0 ||
                    service.__isAdded ||
                    service.__isChanged,
                Deleted: service.Deleted,
            }))
            .filter(
                service =>
                    service.Deleted || service.__isAdded || service.__isChanged
            ),
        HotelZitoneoUrls: (hotelContent.URLs || [])
            .map(url => ({
                Id: url.Id,
                Enabled: url.Enabled,
                IsPrimary: url.IsPrimary,
                Path: url.Path,
                __isAdded: parseInt(url.Id) < 0 || url.__isAdded,
                __isChanged:
                    url.__isChanged ||
                    parseInt(url.Id) < 0 ||
                    url.__isAdded ||
                    url.__isChanged,
                Deleted: url.Deleted,
            }))
            .filter(url => url.Deleted || url.__isAdded || url.__isChanged),
        HotelImages: (hotelContent.HotelImages || [])
            .map(image => ({
                Id: image.Id,
                IdHotelRoomType: image.IdHotelRoomType,
                IdSupplierHotelRoomType: image.IdSupplierHotelRoomType,
                Caption: image.Caption,
                Feature: image.Feature,
                Published: image.Published,
                __isChanged: image.__isChanged || false,
                __isAdded: parseInt(image.Id) < 0 || image.__isAdded,
                Deleted: image.Deleted || false,
            }))
            .filter(
                image => image.Deleted || image.__isAdded || image.__isChanged
            ),
        HotelRoomTypes: (hotelContent.HotelRoomTypes || [])
            .map(room => ({
                Id: room.Id,
                HotelImages: room.HotelImages.map(image => ({
                    Id: image.Id,
                    Caption: image.Caption,
                    Feature: image.Feature,
                    Published: image.Published,
                    IdHotelRoomType: image.IdHotelRoomType,
                    IdSupplierHotelRoomType: image.IdSupplierHotelRoomType,
                    __isAdded: parseInt(image.Id) < 0 || image.__isAdded,
                    __isChanged: image.__isChanged || false,
                    Deleted: image.Deleted || false,
                })).filter(
                    image =>
                        image.Deleted || image.__isAdded || image.__isChanged
                ),
                RoomTypeServices: room.Services.map(service => ({
                    Id: service.Id,
                    IdServiceType: service.IdServiceType,
                    IdHotelRoomType: parseInt(room.Id),
                    Description: service.Description,
                    AttributeDefinitionValues:
                        service.AttributeValues?.map(attributeValue => {
                            const added =
                                parseInt(attributeValue.Id) < 0 ||
                                attributeValue.__isAdded;
                            return {
                                Id: attributeValue.Id,
                                IdAttributeDefinition:
                                    attributeValue.IdAttributeDefinition,
                                Value: attributeValue.Value,
                                __isAdded: added,
                                __isChanged:
                                    attributeValue.__isChanged ||
                                    added ||
                                    false,
                                Deleted: attributeValue.Deleted || false,
                            };
                        })?.filter(
                            roomService =>
                                roomService.Deleted ||
                                roomService.__isAdded ||
                                roomService.__isChanged
                        ) || [],
                    __isAdded: parseInt(service.Id) < 0 || service.__isAdded,
                    __isChanged: service.__isChanged || false,
                    Deleted: service.Deleted || false,
                })).filter(
                    service =>
                        service.Deleted ||
                        service.__isAdded ||
                        service.__isChanged
                ),
                __isChanged: room.__isChanged || false,
            }))
            .filter(room => room.__isChanged),
        IdLogoFile: hotelContent.IdLogoFile,
        IdZitoneoIconFile: hotelContent.IdIconFile,
    };
};
